import { getRandomUUID } from "../Token";
import {
  addTagToDeviceEndPoint,
  fetchDiscoveredCamerasEndPointByCamera,
  getPairedSnapshotEndPoint,
  getUnpairedSnapshotEndPoint,
  pairCameraEndPoint,
  setDeviceNameEndPoint,
  forceDiscoveryAPI,
  updateManualOnboardedEndPoint,
} from "../configuration";
import axiosInstance from "../global/axiosInterceptor";
import {
  getResourceIdentifierForDevice,
  handleApiError,
  encodeOrDecodeRtspCredentials
} from "../global/utility/Utils";
import { getCompanionObjectById } from "../global/utility/deviceTableUtils";
import { getCameraObjectbyId } from "./reportService";
import store from "../store";

export const blurSelected = {
  OBJECT_BLUR: "OBJECT_BLUR",
  FACE_BLUR: "FACE_BLUR",
  NO_BLUR: "NO_BLUR",
};

export const pairCamera = async (
  companionidentifier,
  cameraidentifier,
  activeSession,
  compTagIdsList
) => {
  try {
    const response = await axiosInstance.post(pairCameraEndPoint(), {
      accountname: activeSession.accountId,
      companionidentifier: {
        qrcode: companionidentifier,
      },
      cameraidentifier: {
        qrcode: cameraidentifier,
      },
      tagids: compTagIdsList,
    });
    const res = response.data;
    return res;
  } catch (e) {
    console.error("Error pairing camera:", e);
    return e?.response || [];
  }
};

export const applyDevice = async (
  resourceIdentifier,
  name,
  activeSession,
  site,
  siteTagId,
  floor,
  floorTagId,
  area,
  areaTagId,
  subarea,
  subareaTagId,
  blurValue,
  position
) => {
  try {
    const response = await axiosInstance.patch(addTagToDeviceEndPoint(), {
      accountname: activeSession.accountId,
      resourceIdentifier,
      device: {
        name: name,
        customdata: {
          cameralocation: {
            top: position?.top,
            left: position?.left,
          },
          location: {
            site: { name: site, tag: siteTagId },
            floor: { name: floor, tag: floorTagId },
            area: { name: area, tag: areaTagId },
            subarea: {
              name: subarea ? subarea : "NA",
              tag: subareaTagId ? subareaTagId : "NA",
            },
            blurConfig: blurValue ? blurValue : "NA",
          },
        },
        tagids: [areaTagId, floorTagId, siteTagId],
      },
    });
    const res = response.data;
    return res;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Error patching camera details:", error);
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to patch camera details, please try again."
    );
    return [];
  }
};

export const applyFieldofView = async (
  resourceIdentifier,
  name,
  activeSession
) => {
  try {
    const response = await axiosInstance.patch(addTagToDeviceEndPoint(), {
      accountname: activeSession.accountId,
      resourceIdentifier,
      device: {
        name: name,
      },
    });
    const res = response.data;
    return res;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Error applying field of view:", error);
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to apply field of view, please try again."
    );
    return [];
  }
};

export const updateCameraPosition = async (
  deviceId,
  activeSession,
  position
) => {
  try {
    const response = await axiosInstance.patch(setDeviceNameEndPoint(), {
      accountname: activeSession.accountId,
      resourceidentifier: { deviceId: deviceId },
      device: {
        customdata: {
          cameralocation: {
            top: position.top,
            left: position.left,
          },
        },
      },
    });

    const res = response.data;
    return res;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Error updating camera position:", error);
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to update camera position, please try again."
    );
    return [];
  }
};

export const getUnpairedCamSnapShotIds = async (
  activeSession,
  companionQr,
  cameraQr,
  fovIdArr,
  mediaSource = ""
) => {
  try {
    const fovsArr = fovIdArr.map((fovId) => {
      return { qrcode: fovId };
    });
    const companionDeviceList = store.getState()?.deviceReducer?.companionDeviceList;
    const companionObject = getCompanionObjectById(companionQr, companionDeviceList);
    if (!companionObject) {
      throw new Error(`Companion device not found for QR: ${companionQr}`);
    }
    if (!cameraQr) {
      throw new Error("Missing camera identifier for unpaired snapshot request");
    }
    const response = await axiosInstance.post(getUnpairedSnapshotEndPoint(), {
      accountname: activeSession.accountId,
      companionidentifier: getResourceIdentifierForDevice(companionObject),
      unpaircameraidentifiers: [
        {
          cameraidentifier: {
            qrcode: cameraQr,
          },
          fovs: fovsArr,
        },
      ],
      ...(mediaSource && { mediaSource }),
    });

    const res = response.data;
    return res;
  } catch (error) {
    console.error("Error downloading image:", error);
    throw error;
  }
};

export const getPairedCamSnapShotIds = async (
  activeSession,
  cameraQr,
  fovId = ""
) => {
  try {
    const fovIdMapping = fovId
      ? {
          fovs: {
            fovid: fovId,
          },
        }
      : {};
    const response = await axiosInstance.post(getPairedSnapshotEndPoint(), {
      accountname: activeSession.accountId,
      cameraidentifier: {
        qrcode: cameraQr,
      },
      ...fovIdMapping,
    });

    const res = response.data;
    return res;
  } catch (error) {
    console.error("Error downloading image:", error);
    return [];
  }
};

export const addManualCamera = async (
  qrcode,
  encoders,
  activeSession,
  manualApiResponse = null,
  manualAddStatus = ""
) => {
  try {
    let response;

    if (
      (manualApiResponse && manualApiResponse?.length > 0) ||
      manualAddStatus === "SUCCESS"
    ) {
      response = await axiosInstance.patch(updateManualOnboardedEndPoint(), {
        accountname: activeSession.accountId,
        resourceidentifier: {
          qrcode: qrcode,
        },
        cameras:
          manualApiResponse &&
          manualApiResponse?.map((camera) => ({
            qrcode: camera.qrcode,
            fovs: camera.fovs.map((fov) => ({
              fovid: fov.fovID,
              encoders: encoders.map((encoder) => ({
                encoding: encoder.encoding,
                resolution: encoder.resolution,
                source: fov.source,
                url: encoder.urlRtsp ? encoder?.urlRtsp : encoder?.url,
              })),
            })),
          })),
      });
    } else {
      response = await axiosInstance.post(forceDiscoveryAPI(), {
        accountname: activeSession.accountId,
        resourceidentifier: {
          qrcode: qrcode,
        },
        cameras: [
          {
            fovs: encoders.map((item) => ({
              encoders: [
                {
                  encoding: item?.encoding,
                  resolution: item?.resolution,
                  source: item?.source,
                  url: item?.urlRtsp ? item?.urlRtsp : item?.url //encodeOrDecodeRtspCredentials(item?.urlRtsp ? item?.urlRtsp : item?.url, false),
                },
              ],
            })),
          },
        ],
      });
    }

    let resData = response.data;
    return resData;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error(
      "Failed to refresh camera devices under companion devices:",
      error
    );
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to refresh camera devices under companion devices, please try again."
    );
    //return [];
    throw error;
  }
};

export const fetchCompanionListByCamera = async (activeSession, cameraQR) => {
  try {
    const response = await axiosInstance.post(
      fetchDiscoveredCamerasEndPointByCamera(),
      {
        accountname: activeSession.accountId,
        resourceidentifier: {
          qrcode: cameraQR,
        },
      }
    );

    const res = response.data;
    return res;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Error fetching companion list with camera:", error);
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to get companion list for the camera, please try again."
    );
    return [];
  }
};
